import React, { ChangeEvent, FormEvent, Reducer, useEffect, useReducer, useRef, useState } from 'react';
import { formReducer, calculationReducer, CalculationState, initialCalculationState, FormState, initialFormState, FormStateAction, CalculationAction } from './reducer';
import partReducer, {
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import { renderToStaticMarkup } from 'react-dom/server';
import { text, unitList, speedUnitMap } from '../data';
import { SelectOption } from '../types/types';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import {
	BearingInfo,
	BearingLubricationResult,
	BearingLubricationSpeedsPayload,
	BearingLubricationSpeedsResponse,
	BearingLubricationSpeedsPayloadNonTRB,
	BearingLubricationSpeedsResponseNonTRB,
	TimkenUnit,
} from '../../components/bearing-search/types/types';

import { getBearingSpeedLubricationRatings, getBearingSpeedLubricationRatingsNonTRB } from '../../components/bearing-search/api/api';
import NumberInput from '../../components/bearing-search/components/forms/NumberInput';
import CalculationResults from '../components/CalculationResults';
import PartPreSearch from '../components/PartPreSearch';
import {
	getPartNumberFromUrl,
	getPartStateFromParams,
	toggleImportantNote,
	toggleNextTools,
	toggleReportReady,
} from '../utils/engineering-tools';

// placeholder imports for analytics
import {
	trackStart,
	trackResults,
	updateActionParams,
} from '../utils/analytics';
import DOMPurify from "dompurify";

const getColorForRecommendationId = (id: number): string => {
	const colors: Record<number, string> = {
		0: '#000000',
		1: '#005677',
		2: '#C1D3DA',
		3: '#ff8200',
	};
	return colors[id] || colors[1];
};

const BearingSpeedRatings: React.FC = () => {
	const calculationsRef = useRef<HTMLDivElement>(null);
	const calculationsHeaderRef = useRef<HTMLDivElement>(null);
	const [ isPartSelected, setIsPartSelected ] = useState(false);
	const [ showResults, setShowResults ] = useState(false);
	const [ showSingleRecommendation, setShowSingleRecommendation ] = useState(false);
	const [ isPartOpen, setIsPartOpen ] = useState(false);
	const [ partSearchLookupComplete, setPartSearchLookupComplete ] = useState(false);
	const [ isResultsLoading, setIsResultsLoading ] = useState(false);
	const [ partSearchMessage, setPartSearchMessage ] = useState(text.partWildCardHint);
	const [ lubricationTypes, setLubricationTypes ] = useState<SelectOption<string | number | undefined>[]>([]);
	const [ resultsSubheader, setResultsSubheader ] = useState('');
	const [ lubeRecommendations, setLubeRecommendations ] = useState<BearingLubricationResult[] | null>();
	const [ singleRecommendationText, setSingleRecommendationText ] = useState<string>('');
	const [ singleRecommendationId, setSingleRecommendationId ] = useState<string>('');
	const [ formState, formDispatch ] = useReducer<Reducer<FormState, FormStateAction>>(
		formReducer,
		initialFormState,
	);
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ calculationState, calculationDispatch ] = useReducer<Reducer<CalculationState, CalculationAction>>(
		calculationReducer,
		initialCalculationState,
	);

	useEffect(() => {
		trackStart('bearingSpeedRatings');

		const windowTypes = window?._timken?.lubricationTypes as Record<string, unknown> || null;

		if (windowTypes) {
			setLubricationTypes(mapLubricationTypes(windowTypes));
		}
	}, []);

	useEffect(() => {
		const isShown = showResults && ! isResultsLoading;
		toggleNextTools(isShown);

		// enable the save report dropdown if there are results and the part is selected
		toggleReportReady(
			isShown,
			partState.marketPlaceDescription,
			partState.primaryImage,
		);

		return () => toggleNextTools(false);
	}, [ showResults, isResultsLoading ]);

	// map the lubrication types from the API to the select options
	const mapLubricationTypes = (types: Record<string, unknown>) => {
		const mappedTypes: SelectOption<string>[] = [];
		for (const [ key, value ] of Object.entries(types)) {
			mappedTypes.push({
				id: `lubrication-type-${ key }` as string,
				label: value as string,
				value: key as string,
			});
		}
		return mappedTypes;
	};

	// input element event handlers
	const selectedLubricationTypeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
		formDispatch({
			type: 'change_lubrication_type',
			payload: event.target.value,
		});
	};

	const operatingSpeedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		formDispatch({
			type: 'change_operating_speed',
			payload: event.target.value,
		});
	};

	const selectedUnitHandler = (event: ChangeEvent<HTMLSelectElement>) => {
		const value = event.target.value as TimkenUnit;
		formDispatch({
			type: 'change_unit',
			payload: value,
		});
	};

	const inputPartNumber = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		formDispatch({
			type: 'change_part_number',
			payload: value,
		});
	};

	// begin the part search lookup process
	const partSearchLookup = (event?: FormEvent) => {
		if (event) {
			event.preventDefault();
		}
		setIsPartOpen(true);
	};

	const resetForm = () => {
		partDispatch({
			type: 'reset',
			payload: initialBearingPartState,
		});
		formDispatch({
			type: 'reset',
			payload: initialFormState,
		});

		calculationDispatch({
			type: 'reset',
			payload: initialCalculationState,
		});

		setIsPartSelected(false);
		setPartSearchMessage(text.partWildCardHint);
		setShowResults(false);
		setIsResultsLoading(false);
		setPartSearchLookupComplete(false);
		toggleImportantNote(false);
		resetCalculationsDataAttributes();
		setShowSingleRecommendation(false);
	};

	// update the data and the UI after a part is selected
	const verifyPart = (part?: BearingInfo) => {
		if (part) {
			setIsPartSelected(true);

			setPartSearchMessage(`${ text.bearingType }: ${ part?.typeDescription }`);
			partDispatch({
				type: 'select_part',
				payload: part,
			});
			formDispatch({
				type: 'change_part_number',
				payload: part.marketPlaceDescription || '',
			});
			setPartSearchLookupComplete(true);
		} else {
			setPartSearchLookupComplete(false);
		}
		setIsPartOpen(false);
	};

	const tagSelectedLubricationType = () => {
		const selectedLubricationTypeId = formState.lubricationType;
		if (! selectedLubricationTypeId || selectedLubricationTypeId === 'Select one') {
			return;
		}
		const $recommendationTable = calculationsRef?.current?.querySelector('table');
		const $tableRowLabels = $recommendationTable?.querySelectorAll('tr td:first-child');
		const targetLubricationType = lubricationTypes.find((item) => item.value === selectedLubricationTypeId);
		const targetLubricationText = targetLubricationType?.label;
		const $targetLabel = $tableRowLabels ? Array.from($tableRowLabels).filter((label) => {
			return label.innerHTML === targetLubricationText;
		}) : [];
		$targetLabel[ 0 ]?.parentElement?.classList.add('timken-tool__target-lubrication-type');
	};

	// add data attribute to the icon cells for CSS style targeting
	const tagRecommendationsIcons = () => {
		const $recommendationTable = calculationsRef?.current?.querySelector('table');
		const $iconCells = $recommendationTable?.querySelectorAll('td.center-align');

		$iconCells?.forEach((iconCell) => {
			const textValue = iconCell.innerHTML;
			iconCell.setAttribute('data-icon-id', textValue);
		});
	};

	// generate custom header for the calculation table
	const createTableHeader = () => {
		const headingHTML = (
			<div ref={calculationsHeaderRef} className='timken-tool-bearing-speed-ratings__results-table-header'>
				<div className='timken-tool-bearing-speed-ratings__results-table-header--left'><span>&lt;</span></div>
				<div className='timken-tool-bearing-speed-ratings__results-table-header--center'>
					<h5 className='timken-tool-bearing-speed-ratings__results-table-header--text'>{text.lubricantSystems}</h5>
				</div>
				<div className='timken-tool-bearing-speed-ratings__results-table-header--right'>
					<span>&gt;</span>
				</div>
			</div>
		);
		const container = document.createElement('div');
		container.innerHTML = renderToStaticMarkup(headingHTML);
		const resultsTable = calculationsRef?.current?.querySelector('.c-specs-table') || null;
		resultsTable?.parentElement?.insertBefore(
			container.firstChild as HTMLDivElement,
			resultsTable,
		);
	};

	const getAnalyticsPayload = (payload: BearingLubricationSpeedsPayloadNonTRB | BearingLubricationSpeedsPayload) => {
		const { partNumber, units } = payload;
		const { operatingSpeed, lubricationType } = formState;

		return {
			partNumber,
			units,
			...(operatingSpeed && operatingSpeed !== '0' && { operatingSpeed }),
			...(lubricationType && lubricationType !== 'Select one' && { lubricationType }),
		};
	};

	const handleSpeedCalculationsNonTRB = () => {
		const { bearingType, cupPartNumber } = partState;
		const payload: BearingLubricationSpeedsPayloadNonTRB = {
			partNumber: partState.marketPlaceDescription || '',
			bearingType: bearingType || '',
			cupPartNumber: cupPartNumber || '',
			lubricationTypeID: '0',
			units: speedUnitMap[ formState.units ],
		};
		setShowSingleRecommendation(false);
		setShowResults(true);
		toggleImportantNote(false);
		updateSubheader();
		setIsResultsLoading(true);

		getBearingSpeedLubricationRatingsNonTRB(payload)
			.then((response: BearingLubricationSpeedsResponseNonTRB) => {
				const numberOfResults = response ? 1 : 0;

				const analyticsPayload = getAnalyticsPayload(payload);

				updateActionParams('bearingSpeedRatingsNonTRB', partState);
				trackResults('bearingSpeedRatingsNonTRB', analyticsPayload, numberOfResults, partState.marketPlaceDescription);

				calculationDispatch({
					type: 'update_nonTRB_calculation_data',
					payload: response,
				});
			}).catch((error) => {
				console.error('Error fetching data:', error);
			}).finally(() => {
				setIsResultsLoading(false);
				toggleImportantNote(true);
			});
	};

	// trigger table HTML updates after results are loaded
	useEffect(() => {
		if (partState.bearingType !== 'TRB') {
			return;
		}
		createTableHeader();
		tagRecommendationsIcons();
		tagSelectedLubricationType();
		buildSpeedRatingsLegend();
		applyGradientsToSpeedRatings();
	}, [ calculationState ]);

	useEffect(() => {
		if (isResultsLoading) {
			document.querySelector('.t-engineering-tools__results').scrollIntoView({
				behavior: 'smooth',
			});
		}
	}, [ isResultsLoading ]);

	const checkForTextOnlyRecommendation = (): boolean => {
		return formState.lubricationType !== '' && formState.lubricationType !== 'Select one' && formState.operatingSpeed !== '' && formState.operatingSpeed !== '0';
	};

	const resetCalculationsDataAttributes = () => {
		if (calculationsRef.current) {
			calculationsRef.current.dataset.operatingSpeed = 'false';
			calculationsRef.current.dataset.lubricantType = 'false';
			calculationsRef.current.dataset.singleRecommendation = 'false';
		}
	};

	// get speed lubrication ratings from the API
	const handleSpeedCalculations = (event: FormEvent) => {
		event.preventDefault();
		if (partState.bearingType !== 'TRB') {
			handleSpeedCalculationsNonTRB();
			return;
		}
		const { partNumber, cupPartNumber, bearingType, bearingSubType } = partState;

		const { operatingSpeed, lubricationType } = formState;

		let payload: BearingLubricationSpeedsPayload = {
			partNumber: partNumber || '',
			cupPartNumber: cupPartNumber || '',
			bearingType: bearingType || '',
			bearingSubType: bearingSubType || '',
			units: speedUnitMap[ formState.units ],
		};

		if (lubricationType && lubricationType !== 'Select one') {
			payload = {
				...payload,
				lubricationTypeID: lubricationType,
			};
		}

		if (operatingSpeed && operatingSpeed !== '0') {
			payload = {
				...payload,
				operatingSpeed: parseInt(operatingSpeed, 10),
			};
		}
		updateSubheader();
		setShowResults(true);
		toggleImportantNote(false);
		setIsResultsLoading(true);
		setShowSingleRecommendation(false);

		resetCalculationsDataAttributes();

		getBearingSpeedLubricationRatings(payload)
			.then((response: BearingLubricationSpeedsResponse) => {
					if (response) {
						updateActionParams('bearingSpeedRatings', partState);
						calculationDispatch({
							type: 'update_calculation_data',
							payload: response.lubeRecommendations || [],
						});
						setLubeRecommendations(response.lubeRecommendations);

						setIsResultsLoading(false);
						toggleImportantNote(true);

						formDispatch({
							type: 'change_operating_speed',
							payload: formState.operatingSpeed ? formState.operatingSpeed : '0',
						});
						const numberOfResults = response.lubeRecommendations ? 1 : 0;
						const analyticsPayload = getAnalyticsPayload(payload);
						trackResults('bearingSpeedRatings', analyticsPayload, numberOfResults, partState.marketPlaceDescription);
						updateActionParams('bearingSpeedRatings', partState);
						if (checkForTextOnlyRecommendation() && response.recommendationString) {
							setSingleRecommendationId(Object.values(response.lubeRecommendations)[0].recommendationId.toString());
							setSingleRecommendationText(Object.values(response.recommendationString)[0]);
						}
					}
				},
			)
			.catch((error) => {
				console.error('Error fetching data:', error);
			},
			).finally(() => {
				setIsResultsLoading(false);
				tagSingleSpeedResult();
				tagSingleLubricantResult();
				tagSingleRecommendationResult();
				setShowSingleRecommendation(checkForTextOnlyRecommendation());
			});
	};

	const tagSingleRecommendationResult = () => {
		if (calculationsRef.current && checkForTextOnlyRecommendation()) {
			calculationsRef.current.dataset.singleRecommendation = 'true';
		}
	};

	const tagSingleSpeedResult = () => {
		const isSingleSpeedRequest = formState.operatingSpeed && formState.operatingSpeed !== '' && formState.operatingSpeed !== '0';
		const hasEmptyLubricationType = ! formState.lubricationType || formState.lubricationType === 'Select one';
		if (isSingleSpeedRequest && hasEmptyLubricationType && calculationsRef.current) {
			calculationsRef.current.dataset.operatingSpeed = 'true';
		}
	};

	const tagSingleLubricantResult = () => {
		const isSingleLUbricantRequest = formState.lubricationType && formState.lubricationType !== 'Select one';
		const hasEmptyOperatingSpeed = ! formState.operatingSpeed || formState.operatingSpeed === '' || formState.operatingSpeed === '0';
		if (isSingleLUbricantRequest && hasEmptyOperatingSpeed && calculationsRef.current) {
			calculationsRef.current.dataset.lubricantType = 'true';
		}
	};

	const updateSubheader = () => {
		setResultsSubheader('');
		const subheaderText = `${ text.speedRating } ${ formState.partNumber } (${ partState.typeDescription })`;
		setResultsSubheader(subheaderText);
	};

	const setPartFromPreSearchAndSubmit = (value: string, e: React.MouseEvent<HTMLElement>) => {
		formDispatch({
			type: 'change_part_number',
			payload: value,
		});

		setIsPartSelected(true);
		partSearchLookup(e);
	};

	const buildResultTableClasses = () => {
		let classes = 'timken-tool-bearing-speed-ratings__results ';
		classes += formState.units === 'M' ? 'timken-tool-bearing-speed-ratings__results--metric ' : 'timken-tool-bearing-speed-ratings__results--imperial ';
		classes += isResultsLoading ? ' loading ' : '';
		classes += partState.bearingType !== 'TRB' ? 't-bearing-type-non-trb' : 't-bearing-type-trb ';
		return classes;
	};

	const buildSpeedRatingsLegend = () => {
		// From: wp-content/themes/timken/template-parts/engineering-tools/bearing-speed-ratings.php
		const staticGuide = document.querySelector('.t-engineering-tools__speed-ratings + .timken-tool-bearing-speed-ratings__info__wrapper .timken-tool-bearing-speed-ratings__results-guide:not(.timken-tool-bearing-speed-ratings__results-guide__updated)');

		const targetElement = calculationsRef?.current?.querySelector('.t-engineering-tools__results');
		const existingGuide = targetElement?.querySelector('.timken-tool-bearing-speed-ratings__results-guide__updated');

		if (!targetElement) {
			return;
		}

		if (existingGuide) {
			existingGuide.remove();
		}

		const legend = (
			<div className="timken-tool-bearing-speed-ratings__results-guide timken-tool-bearing-speed-ratings__results-guide__updated">
				<h5 className='p-3 timken-tool-bearing-speed-ratings__results-guide--heading'>Speed Capability Guideline</h5>
				<ul className='timken-tool-bearing-speed-ratings__results-guide--list'>
					{checkForTextOnlyRecommendation() ? (
						<li data-icon-id={singleRecommendationId}>
							<span className='timken-tool-bearing-speed-ratings__results-guide--marker'></span>
							<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(singleRecommendationText) }} />
						</li>
					) : (
						<>
							<li data-icon-id="1">
								<span className='timken-tool-bearing-speed-ratings__results-guide--marker'></span>
								Industry experience indicates no problems under normal operating conditions
							</li>
							<li data-icon-id="2">
								<span className='timken-tool-bearing-speed-ratings__results-guide--marker'></span>
								Industry experience indicates testing may be required to optimize a system
							</li>
							<li data-icon-id="3">
								<span className='timken-tool-bearing-speed-ratings__results-guide--marker'></span>
								Testing will be needed and special bearings may be required to achieve these speeds
							</li>
							<li data-icon-id="0">
								<span className='timken-tool-bearing-speed-ratings__results-guide--marker'></span>
								<span>Speed rating exceeded for specified lubrication system and operating speed. <a
									href='https://locations.timken.com/sales-locator/' target='_blank'
									rel="noreferrer">Consult your Timken Sales Office.</a>
						</span>
							</li>
						</>
					)}
				</ul>
			</div>
		);

		const container = document.createElement('div');
		container.innerHTML = renderToStaticMarkup(legend);
		targetElement.appendChild(container.firstChild);

		if (staticGuide) {
			staticGuide.classList.add('hidden');
		}
	};

	const applyGradientsToSpeedRatings = () => {
		const $recommendationTable = calculationsRef?.current?.querySelector('table');
		const $dataCells = $recommendationTable?.querySelectorAll('td[data-icon-id]');

		if (! $dataCells || $dataCells.length === 0) {
			return;
		}

		$dataCells.forEach((cell) => {
			const cellContent = cell.textContent?.trim() || '';

			if (! cellContent.includes(':::')) {
				return;
			}

			try {
				const [ finalId, initialId, transitionsJSON ] = cellContent.split(':::');
				const initialRecommendationId = parseInt(initialId, 10);
				const transitions = JSON.parse(transitionsJSON);

				cell.textContent = '';
				cell.setAttribute('data-icon-id', finalId);

				if (! transitions || transitions.length === 0) {
					return;
				}

				if (initialRecommendationId === 0) {
					return;
				}

				const gradientParts: string[] = [];
				let currentId = initialRecommendationId;

				gradientParts.push(`${ getColorForRecommendationId(currentId) } 0%`);

				transitions.forEach((transition: { position: number; speed: number; recommendationId: number }) => {
					const { position, recommendationId: newId } = transition;

					gradientParts.push(`${ getColorForRecommendationId(currentId) } ${ position.toFixed(2) }%`);
					gradientParts.push(`${ getColorForRecommendationId(newId) } ${ position.toFixed(2) }%`);

					currentId = newId;
				});

				gradientParts.push(`${ getColorForRecommendationId(currentId) } 100%`);

				const gradient = `linear-gradient(to right, ${ gradientParts.join(', ') })`;

				(cell as HTMLElement).style.setProperty('--cell-gradient', gradient);
				(cell as HTMLElement).classList.add('has-gradient');

			} catch (e) {
				console.error('Error parsing transitions for cell:', cellContent, e);
			}
		});
	};

	useEffect(() => {
		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				formDispatch({
					type: 'change_part_number',
					payload: partNumberFromUrl,
				});
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
				partSearchLookup();
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				formDispatch({
					type: 'change_part_number',
					payload: savedPart,
				});
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setIsPartSelected( true );
			}
		}



	}, []);

	return (
		<>
			<div className="timken-tool-bearing-speed-ratings">

				<form onSubmit={partSearchLookup}>
					<div className="timken-tool-bearing-speed-ratings__form-group row">
						<div className='col-12 col-md-3 timken-tool-presearch-container'>
							<TextInput
								label={text.partNumber}
								value={formState.partNumber}
								onChange={inputPartNumber}
								id="bearing_part_number"
								name="partNumber"
								placeholder={text.partNumber}
								message={partSearchMessage}
								disabled={isPartSelected}
								verified={isPartSelected}
								required={true}
							/>
							<PartPreSearch
								input={formState.partNumber}
								isHidden={formState.partNumber.length < 2 || isPartSelected || isPartOpen}
								unit={formState.units}
								verifyPartNumber={(value: string, e) => setPartFromPreSearchAndSubmit(value, e)}
							/>
						</div>
						{(!partSearchLookupComplete || partState.bearingType === 'TRB') && (
							<div className='col-12 col-md-3'>
								<SelectDropdown
									label={text.displayUnits}
									value={formState.units}
									onChange={selectedUnitHandler}
									id="display-unit"
									name="display-unit"
									options={unitList}
									required={true}
								/>
							</div>
						)}
						{
							! partSearchLookupComplete && (
								<div className="col-12 col-md-3">
									<button
										className="btn btn--primary btn--form-row"
										type="submit"
										disabled={formState.partNumber.length < 2}
									>
										{text.lookup}
									</button>
								</div>
							)
						}
					</div>
				</form>
				{partSearchLookupComplete && (
					<form onSubmit={handleSpeedCalculations}>
						{partSearchLookupComplete && (
							<>
								{partState.bearingType === 'TRB' && (
									<div className="row">
										<div className='col-12 col-md-3'>
											<NumberInput
												label={text.operatingSpeed}
												value={formState.operatingSpeed}
												unitLabel='rpm'
												min={0}
												onChange={operatingSpeedHandler}
												id="operating-speed"
												name="operating-speed"
												placeholder=""
											/>
										</div>
										<div className="col-12 col-md-3">
											<SelectDropdown
												label={text.lubricationType}
												value={formState.lubricationType}
												placeholder='Select one'
												onChange={selectedLubricationTypeHandler}
												id="lubrication-type"
												name="lubrication-type"
												options={lubricationTypes}
											/>
										</div>
									</div>
								)}

								<div className="t-engineering-tools__terms-of-use"
									 dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
								/>
								<div className="t-engineering-tools__search-actions">
									<button
										className="btn btn--primary"
										type="submit"
										disabled={isResultsLoading}
									>
										{text.calculate}
									</button>
									<button
										className="btn btn--secondary"
										type="button"
										onClick={resetForm}
										disabled={isResultsLoading}
									>
										{text.clear}
									</button>
								</div>
							</>
						)}

					</form>
				)}

			</div>
			{isPartOpen && !! formState.partNumber && (
				<PartSearch
					units={formState.units}
					partNumber={formState.partNumber}
					onSelect={verifyPart}
					onClose={() => {
						setIsPartOpen(false);
						setPartSearchLookupComplete(false);
					}}
				/>
			)}

			{showResults && (
				<div className={buildResultTableClasses()} ref={calculationsRef}>
					<div className="timken-tool-bearing-speed-ratings__results-table">
						<CalculationResults
							data={[calculationState]}
							subtitle={resultsSubheader}
							loading={isResultsLoading}
						/>
					</div>

				</div>
			)}
		</>
	);
};

export default BearingSpeedRatings;
